import { ChevronLeft } from "lucide-react";
import HelpGenieLogo from "@/components/ui/HelpGenieLogo";
import { GenieSwitcher } from "@/components/suite/GenieSwitcher";

/**
 * /internal/switcher-demo — visual reference for the shared GenieSwitcher.
 *
 * Stage 1 replicates the Voice sidebar header (exact classes from Sidebar.tsx)
 * so header fit can be checked at the real w-72 width. Stage 2 shows the same
 * component as the satellite apps (Mail/Sync/Outbound) should mount it in
 * their top bars — this is the reference for those teams.
 */
const SwitcherDemo = () => (
  <div className="min-h-screen bg-genie-background p-8 space-y-10">
    <div>
      <h1 className="text-xl font-bold text-genie-logo mb-1">GenieSwitcher — internal demo</h1>
      <p className="text-sm text-genie-muted">
        Stage 1: Voice sidebar header at real width (w-72). Stage 2: satellite top-bar mount.
      </p>
    </div>

    {/* Stage 1 — Voice sidebar header replica (classes mirror Sidebar.tsx) */}
    <section data-testid="stage-sidebar">
      <h2 className="text-xs font-semibold uppercase tracking-widest text-genie-muted mb-3">
        In Voice — sidebar header
      </h2>
      <div className="w-72 rounded-xl border border-genie-border bg-gradient-to-b from-[#F9FAFC] to-white shadow-sm overflow-hidden">
        <div className="px-4 pt-4 pb-4">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center min-w-0">
              <HelpGenieLogo size={36} />
              <span className="ml-3 text-2xl font-bold tracking-tight text-genie-logo whitespace-nowrap">
                Help Genie
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <GenieSwitcher />
              <button
                className="rounded-full p-1.5 hover:bg-genie-primary/10 hover:text-genie-primary transition-all duration-200"
                aria-label="Collapse sidebar"
                type="button"
              >
                <ChevronLeft size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Stage 2 — how satellites (Mail here) should mount the same component */}
    <section data-testid="stage-topbar">
      <h2 className="text-xs font-semibold uppercase tracking-widest text-genie-muted mb-3">
        In a satellite app — top bar (reference for Mail / Sync / Outbound)
      </h2>
      <div className="max-w-3xl rounded-xl border border-genie-border bg-white shadow-sm">
        <div className="flex h-14 items-center gap-3 px-4">
          <HelpGenieLogo size={26} />
          <span className="font-bold text-genie-logo whitespace-nowrap">
            Help Genie{" "}
            <span className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text pr-0.5 italic text-transparent">
              Mail
            </span>
          </span>
          <span className="ml-auto flex items-center gap-2.5">
            <GenieSwitcher currentAppId="mail" />
            <span
              className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold text-white"
              style={{ background: "linear-gradient(135deg, #4E9CFF 0%, #FF4DB8 100%)" }}
            >
              S
            </span>
          </span>
        </div>
      </div>
    </section>
  </div>
);

export default SwitcherDemo;
